<template>
  <div class="d-flex flex-column h-100 overflow-hidden">

    <!-- Top toolbar -->
    <div class="kds-toolbar flex-shrink-0 d-flex align-items-center justify-content-between gap-2 px-3">
      <span
        class="kds-status-pill"
        :class="socketConnected ? 'kds-status-pill--online' : 'kds-status-pill--offline'"
        :title="socketConnected ? $t('Live') : ($t('Offline') + ' — ' + $t('Reconnecting') + '...')"
      >
        <span class="kds-status-dot"></span>
        <template v-if="socketConnected">{{ $t('Live') }}</template>
        <template v-else>
          <span>{{ $t('Offline') }}</span>
          <span class="kds-spinner"></span>
        </template>
      </span>

      <div class="d-flex align-items-center gap-2">
        <!-- Fullscreen toggle (icon only) — ซ่อนถ้าเบราว์เซอร์ไม่รองรับ/ไม่อนุญาต -->
        <button
          v-if="fsSupported"
          type="button"
          class="kds-refresh-btn"
          :title="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
          :aria-label="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
          @click="toggleFs"
        >
          <i class="fa-solid" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"/>
        </button>

        <!-- Manual refresh button (icon only) -->
        <button
          type="button"
          class="kds-refresh-btn"
          :disabled="refreshing"
          :title="$t('Refresh')"
          :aria-label="$t('Refresh')"
          @click="fetchOrders"
        >
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': refreshing }"/>
        </button>
      </div>
    </div>

    <!-- Mobile Tab Bar -->
    <div class="d-lg-none kds-tab-bar flex-shrink-0">
      <button
        v-for="tab in tabs"
        :key="tab.state"
        class="kds-tab"
        :class="{ 'active': activeTab === tab.state }"
        @click="activeTab = tab.state"
      >
        <i :class="tab.icon" class="me-1"/>
        {{ $t(tab.label) }}
        <span class="kds-tab-badge">{{ ordersByState[tab.state].length }}</span>
      </button>
    </div>

    <!-- Desktop: 3 Columns | Mobile: Single active column (slides between tabs) -->
    <div
      ref="board"
      class="flex-grow-1 overflow-hidden kds-board"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
    >
     <div
       class="kds-board-track d-flex h-100"
       :class="{ 'is-dragging': dragging }"
       :style="{ '--kds-slide': boardOffset }"
     >

      <!-- Column: Queued -->
      <div
        class="kds-column d-flex flex-column border-end border-secondary"
      >
        <div class="kds-column-header queued px-3 py-2 flex-shrink-0 d-none d-lg-flex align-items-center justify-content-between">
          <span><i class="fa-solid fa-hourglass-half me-2"/>{{ $t('_POS.Queued') }}</span>
          <span class="badge bg-light text-dark">{{ ordersByState.queued.length }}</span>
        </div>
        <div class="flex-grow-1 overflow-y-auto p-2 kds-column-body">
          <div v-if="loading" class="text-center text-muted py-5">
            <i class="fa-solid fa-spinner fa-spin fa-2x text-secondary"/>
          </div>
          <template v-else>
            <kds-order-card
              v-for="order in ordersByState.queued"
              :key="'q-' + order.id"
              :order="order"
              class="mb-2"
              @update-state="updateItemState"
              @update-item-state="updateSingleItemState"
              @update-items-state="updateItemsState"
              @restock-item="restockItem"
            />
            <div v-if="ordersByState.queued.length === 0" class="py-5 small text-center text-white">
              <i class="fa-solid fa-inbox fa-2x mb-2 opacity-25"/><br>
              {{ $t('No items') }}
            </div>
          </template>
        </div>
      </div>

      <!-- Column: Progressing -->
      <div
        class="kds-column d-flex flex-column border-end border-secondary"
      >
        <div class="kds-column-header progressing px-3 py-2 flex-shrink-0 d-none d-lg-flex align-items-center justify-content-between">
          <span><i class="fa-solid fa-fire-burner me-2"/>{{ $t('_POS.Cooking') }}</span>
          <span class="badge bg-dark text-white">{{ ordersByState.progressing.length }}</span>
        </div>
        <div class="flex-grow-1 overflow-y-auto p-2 kds-column-body">
          <div v-if="loading" class="text-center text-muted py-5">
            <i class="fa-solid fa-spinner fa-spin fa-2x text-secondary"/>
          </div>
          <template v-else>
            <kds-order-card
              v-for="order in ordersByState.progressing"
              :key="'p-' + order.id"
              :order="order"
              class="mb-2"
              @update-state="updateItemState"
              @update-item-state="updateSingleItemState"
              @update-items-state="updateItemsState"
              @restock-item="restockItem"
            />
            <div v-if="ordersByState.progressing.length === 0" class="py-5 small text-center text-white">
              <i class="fa-solid fa-inbox fa-2x mb-2 opacity-25"/><br>
              {{ $t('No items') }}
            </div>
          </template>
        </div>
      </div>

      <!-- Column: Ready -->
      <div
        class="kds-column d-flex flex-column"
      >
        <div class="kds-column-header ready px-3 py-2 flex-shrink-0 d-none d-lg-flex align-items-center justify-content-between">
          <span><i class="fa-solid fa-bell-concierge me-2"/>{{ $t('_POS.Ready') }}</span>
          <span class="badge bg-light text-dark">{{ ordersByState.ready.length }}</span>
        </div>
        <div class="flex-grow-1 overflow-y-auto p-2 kds-column-body">
          <div v-if="loading" class="text-center text-muted py-5">
            <i class="fa-solid fa-spinner fa-spin fa-2x text-secondary"/>
          </div>
          <template v-else>
            <kds-order-card
              v-for="order in ordersByState.ready"
              :key="'r-' + order.id"
              :order="order"
              class="mb-2"
              @update-state="updateItemState"
              @update-item-state="updateSingleItemState"
              @update-items-state="updateItemsState"
              @restock-item="restockItem"
            />
            <div v-if="ordersByState.ready.length === 0" class="py-5 small text-center text-white">
              <i class="fa-solid fa-inbox fa-2x mb-2 opacity-25"/><br>
              {{ $t('No items') }}
            </div>
          </template>
        </div>
      </div>

     </div>
    </div>
  </div>
</template>

<script>
import { watch } from 'vue';
import KdsOrderCard from './KdsOrderCard.vue';
import { useKDSSocket } from '@/composables/useKDSSocket';
import { useKDSStore }  from '@/stores/kds.store';
import { KDSOrders }    from '@/services/endpoint/kds-orders.service';
import { playNotifySound, NOTIFY_SOUNDS } from '@/composables/useNotifySound';
import { isFullscreen, isFullscreenSupported, toggleFullscreen, onFullscreenChange } from '@/utils/fullscreen';

// Normalize an API item → KDS card shape, and pre-compute ingredient usage.
// split_portion rule (mirror StockDeductionHelper::deductStock ฝั่ง BE):
//   divisor = จำนวน option ที่เลือกในกลุ่มซึ่ง "มีสูตรและไม่ split_exempt"
//   split_exempt = ปริมาณคงที่ ไม่ถูกหารและไม่นับเป็นตัวหาร
//   solo = เป็น option เดียวที่มีสูตรที่ถูกเลือกในกลุ่ม → ใช้ rec.quantity_solo แทน (ถ้าตั้งไว้)
// Quantities are stored PER PORTION (per single dish), so the kitchen popup
// shows the recipe ratio for one plate. Multiply by item.quantity at the call
// site if a total is needed (e.g. restock for cancelled portion).
function normalizeItem(raw) {
  const orderedQty   = parseFloat(raw.quantity) || 1;
  const cancelledQty = parseFloat(raw.cancelled_quantity) || 0;
  const effectiveQty = Math.max(0, orderedQty - cancelledQty);

  // Count per group: ตัวหาร (มีสูตร + ไม่ exempt) และจำนวน option ที่มีสูตร (ไว้เช็คโหมดเดี่ยว)
  const groupShareCount = {};
  const groupRecipeCount = {};
  (raw.options || []).forEach(o => {
    if (o.group_id == null || !(o.recipes || []).length) return;
    groupRecipeCount[o.group_id] = (groupRecipeCount[o.group_id] || 0) + 1;
    if (!o.split_exempt) groupShareCount[o.group_id] = (groupShareCount[o.group_id] || 0) + 1;
  });

  const ingredients = [];

  // Main product inventories — per portion (1 dish)
  (raw.inventories || []).forEach(inv => {
    ingredients.push({
      name: inv.name,
      type: inv.type,
      quantity: parseFloat(inv.quantity) || 0,
      unit: inv.unit,
      source: 'product',
    });
  });

  // Option recipes — per portion (1 dish), pre-divided by split_portion ratio
  (raw.options || []).forEach(opt => {
    const isSplit = opt.recipe_behavior === 'split_portion' && opt.group_id != null;
    const divisor = (isSplit && !opt.split_exempt && groupShareCount[opt.group_id] > 0)
      ? groupShareCount[opt.group_id]
      : 1;
    const isSolo = isSplit && groupRecipeCount[opt.group_id] === 1;
    (opt.recipes || []).forEach(rec => {
      const baseQty = (isSolo && parseFloat(rec.quantity_solo) > 0)
        ? parseFloat(rec.quantity_solo)
        : (parseFloat(rec.quantity) || 0);
      ingredients.push({
        name: rec.name,
        quantity: baseQty / divisor,
        unit: rec.unit,
        source: 'option',
        option_name: opt.name,
        option_name_en: opt.name_en,
        group_name: opt.group_name,
        group_name_en: opt.group_name_en,
      });
    });
  });

  return {
    id: raw.id,
    product_name: raw.name,
    product_name_en: raw.name_en,
    product_category_id: raw.product_category_id ?? null, // ใช้กรองตาม KDS station (realtime)
    quantity: effectiveQty,
    ordered_quantity: orderedQty,
    cancelled_quantity: cancelledQty,
    cancelled_amount: parseFloat(raw.cancelled_amount) || 0,
    cancelled_reason: raw.cancelled_reason || '',
    restocked_quantity: parseFloat(raw.restocked_quantity) || 0,
    state: (raw.state || 'queued').toLowerCase(),
    remark: raw.remark || '',
    is_reward: !!raw.is_reward,
    modifiers: (raw.options || []).map(o => ({ name: o.name, name_en: o.name_en, group_name: o.group_name, group_name_en: o.group_name_en })),
    ingredients,
  };
}

function normalizeOrder(raw) {
  return {
    id: raw.id,
    queue_no: raw.queue,
    session_no: raw.session_no,
    type: raw.type,
    table_name: raw.table,
    customer_name: raw.name,
    state: (raw.state || '').toLowerCase() === 'pending' ? 'queued' : (raw.state || 'queued').toLowerCase(),
    created_at: raw.created_at,
    created_by: raw.created_by,
    items: (raw.items || []).map(normalizeItem),
  };
}

export default {
  name: 'KDSDisplay',
  components: {
    KdsOrderCard
  },
  setup() {
  },
  data() {
    return {
      loading: false,
      refreshing: false,
      socketConnected: false,   // mirror ของ ref จาก useKDSSocket (options API ไม่ unwrap ref ที่ยัดใส่ this)
      stopWatchSocket: null,
      socketEnsureConnected: null,
      fsSupported: isFullscreenSupported(),
      isFullscreen: isFullscreen(),
      removeFsListener: null,
      orders: [],
      pollingInterval: null,
      touchStartX: 0,
      touchStartY: 0,
      dragging: false,   // finger is actively dragging the track horizontally
      dragDx: 0,         // current horizontal drag offset in px
      boardWidth: 0,     // viewport width, measured on touchstart
      lockDir: null,     // 'h' | 'v' | null — gesture axis once committed
      activeTab: 'queued',
      tabs: [
        { state: 'queued',      label: '_POS.Queued',    icon: 'fa-solid fa-hourglass-half' },
        { state: 'progressing', label: '_POS.Cooking',   icon: 'fa-solid fa-fire-burner' },
        { state: 'ready',       label: '_POS.Ready',     icon: 'fa-solid fa-bell-concierge' },
      ]
    };
  },
  computed: {
    // Mobile slide offset: shift the track left by one viewport per tab index.
    // Consumed by the .kds-board-track transform (mobile media query only).
    // While dragging, return a px value that follows the finger in real time;
    // otherwise snap to the active tab in %.
    boardOffset() {
      const idx = Math.max(0, this.tabs.findIndex(t => t.state === this.activeTab));
      if (this.dragging) {
        return `${-idx * this.boardWidth + this.dragDx}px`;
      }
      return `-${idx * 100}%`;
    },
    ordersByState() {
      // Hide items that have nothing left to cook (fully cancelled)
      const isCookable = (i) => i.quantity > 0;
      const buildCards = (state) => {
        const cards = [];
        for (const order of this.orders) {
          const cookableItems = order.items.filter(isCookable);
          if (state === 'progressing') {
            // Show card if at least one item is still progressing
            // but include ALL items (progressing + ready) so ready ones show as strikethrough
            const hasProgressing = cookableItems.some(i => (i.state || order.state) === 'progressing');
            if (!hasProgressing) continue;
            const items = cookableItems.filter(i => {
              const s = i.state || order.state;
              return s === 'progressing' || s === 'ready';
            });
            if (items.length === 0) continue;
            cards.push({ ...order, state, items });
          } else if (state === 'ready') {
            // Show card if at least one item is still ready
            // but include completed items too so they show as strikethrough
            const hasReady = cookableItems.some(i => (i.state || order.state) === 'ready');
            if (!hasReady) continue;
            const items = cookableItems.filter(i => {
              const s = i.state || order.state;
              return s === 'ready' || s === 'completed';
            });
            if (items.length === 0) continue;
            cards.push({ ...order, state, items });
          } else {
            const items = cookableItems.filter(i => (i.state || order.state) === state);
            if (items.length === 0) continue;
            cards.push({ ...order, state, items });
          }
        }
        return cards;
      };
      return {
        queued:      buildCards('queued'),
        progressing: buildCards('progressing'),
        ready:       buildCards('ready'),
      };
    }
  },
  methods: {
    async toggleFs() {
      try {
        await toggleFullscreen();
      } catch (e) {
        // บางเบราว์เซอร์บล็อก fullscreen — เงียบไว้
      }
      this.isFullscreen = isFullscreen();
    },
    async fetchOrders() {
      if (this.refreshing) return;
      this.refreshing = true;
      if (!this.orders.length) this.loading = true;
      try {
        const res = await KDSOrders.list();
        this.orders = (res.data.data || []).map(normalizeOrder);
      } catch (e) {
        // keep previous orders on failure
      } finally {
        this.loading = false;
        this.refreshing = false;
      }
    },
    async restockItem(orderId, itemId, restockQty) {
      const order = this.orders.find(o => o.id === orderId);
      if (!order) return;
      const item = order.items.find(i => i.id === itemId);
      if (!item) return;

      const qty = parseInt(restockQty) || 0;
      if (qty <= 0) return;

      // Optimistic counter increment — revert on failure
      const prev = item.restocked_quantity;
      item.restocked_quantity = (prev || 0) + qty;

      await KDSOrders.restockItem(itemId, { restock_quantity: qty }).then(() => {
        this.$toast.success(this.$t('Ingredients restocked'));
      }).catch(err => {
        item.restocked_quantity = prev;
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      });
    },
    async updateSingleItemState(orderId, itemId, newState) {
      const order = this.orders.find(o => o.id === orderId);
      if (!order) return;
      const item = order.items.find(i => i.id === itemId);
      if (!item) return;
      const prevState = item.state;
      item.state = newState;
      await KDSOrders.updateItemState(itemId, { state: newState }).catch(err => {
        // ส่วนใหญ่คือ item ถูกยกเลิก/ลบไปแล้ว แต่จอนี้ยังถือของเก่า — คืนค่าเดิมแล้ว resync
        item.state = prevState;
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Some items changed — list refreshed'));
        this.fetchOrders();
      });
    },
    async updateItemState(orderId, newState) {
      const order = this.orders.find(o => o.id === orderId);
      if (!order) return;

      // Each bulk transition only applies to items in the matching prior state.
      const fromState = {
        progressing: 'queued',
        ready: 'progressing',
        completed: 'ready',
      }[newState];

      // Only the items currently in `fromState` should move — never the rest
      // of the order (e.g. items still progressing must stay progressing).
      const targetIds = order.items
        .filter(item => (item.state || order.state) === fromState)
        .map(item => item.id);

      await this.updateItemsState(orderId, targetIds, newState);
    },
    // Bulk transition: one request, one backend transaction, so the order's
    // own state stays consistent (looping per-item could leave it un-updated).
    async updateItemsState(orderId, ids, newState) {
      const order = this.orders.find(o => o.id === orderId);
      if (!order || !ids?.length) return;

      // Optimistic update
      order.items.forEach(item => {
        if (ids.includes(item.id)) item.state = newState;
      });

      await KDSOrders.updateItemsState(orderId, { ids, state: newState }).then(res => {
        // BE ข้าม item ที่ถูกยกเลิก/ลบไปแล้ว (จอนี้ถือ snapshot เก่าตอนหลับ) → ดึงรายการใหม่ให้ตรง
        // JsonResource ห่อด้วยคีย์ 'data' (เหมือน list ที่อ่าน res.data.data)
        if (res?.data?.data?.skipped_ids?.length) {
          this.$toast.warn(this.$t('Some items changed — list refreshed'));
          this.fetchOrders();
        }
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
        this.fetchOrders();
      });
    },
    activeIndex() {
      return Math.max(0, this.tabs.findIndex(t => t.state === this.activeTab));
    },
    onTouchStart(e) {
      const t = e.changedTouches[0];
      this.touchStartX = t.clientX;
      this.touchStartY = t.clientY;
      this.boardWidth = this.$refs.board?.clientWidth || window.innerWidth;
      this.dragDx = 0;
      this.lockDir = null;
      this.dragging = false;
    },
    onTouchMove(e) {
      const t = e.changedTouches[0];
      const dx = t.clientX - this.touchStartX;
      const dy = t.clientY - this.touchStartY;

      // Decide the gesture axis once movement is intentional, then lock it so a
      // vertical scroll inside a column is never hijacked into a tab change.
      if (!this.lockDir) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        this.lockDir = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (this.lockDir === 'h') this.dragging = true;
      }
      if (this.lockDir !== 'h') return;

      // Horizontal drag: take over from the browser (stops back-swipe / scroll).
      e.preventDefault();

      const idx = this.activeIndex();
      let d = dx;
      // Rubber-band at the edges so there's nothing to "fall back" from.
      if ((idx === 0 && d > 0) || (idx === this.tabs.length - 1 && d < 0)) {
        d = d / 3;
      }
      this.dragDx = d;
    },
    onTouchEnd() {
      if (this.lockDir !== 'h') { this.lockDir = null; return; }

      const idx = this.activeIndex();
      const threshold = this.boardWidth * 0.2; // pass 20% of the width to commit
      let next = idx;
      if (this.dragDx <= -threshold && idx < this.tabs.length - 1) next = idx + 1;
      else if (this.dragDx >= threshold && idx > 0) next = idx - 1;

      // Drop back to %-based offset (transition re-enabled) and snap into place.
      this.dragging = false;
      this.dragDx = 0;
      this.lockDir = null;
      this.activeTab = this.tabs[next].state;
    },
    startPolling() {
      if (this.pollingInterval) return;
      this.pollingInterval = setInterval(() => {
        this.fetchOrders();
      }, 15000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    // ─── Resync ───────────────────────────────────────────────────────────────
    // จอที่ไม่ได้ต่อ socket อยู่ = พลาด event ทุกตัวในช่วงนั้น (Pusher ไม่ replay ย้อนหลัง)
    // เคสหลัก: มือถือล็อกหน้าจอ/สลับแอป แล้วกลับมา — snapshot บนจอเป็นของเก่า
    // กดปุ่มต่อจะยิง state ผิดตัว จึงต้องดึงรายการใหม่ทุกครั้งที่ "กลับมาเห็นจอ"
    resyncIfVisible() {
      if (document.visibilityState !== 'visible') return;
      this.socketEnsureConnected?.();
      this.fetchOrders();
      // socket หลุดอยู่ → เดินหน้าด้วย polling ไปก่อนจนกว่าจะต่อกลับได้
      if (this.socketConnected) this.stopPolling();
      else this.startPolling();
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.resyncIfVisible();
      else this.stopPolling(); // จอดับ/สลับแอป — ไม่ต้องยิง request ทิ้ง
    },
    onSocketReconnect() {
      this.stopPolling();
      this.fetchOrders();
    },
    websocketAddOrder(data) {
      const order = normalizeOrder(data);
      // กรองตาม KDS station: เห็นเฉพาะ item หมวดที่สถานีดูแล (categoryIds ว่าง = All = ไม่กรอง)
      const { categoryIds } = useKDSStore();
      if (categoryIds && categoryIds.length) {
        order.items = order.items.filter(it => categoryIds.includes(it.product_category_id));
        if (!order.items.length) return; // ออเดอร์นี้ไม่มีรายการของสถานีนี้ → ไม่ขึ้นจอ
      }
      this.orders.push(order);
      playNotifySound(NOTIFY_SOUNDS.newOrder);
    },
    websocketCancelItem(data) {
      const orderId = Number(data.order_id);
      const itemId = Number(data.id);
      const order = this.orders.find(i => i.id === orderId);
      // ออเดอร์ไม่อยู่บนจอนี้ (คนละสถานี หรือจอเพิ่งตื่นแล้วยังไม่ resync) — ปล่อยผ่าน ไม่ให้ handler พัง
      if (order === undefined) return;
      const item = order.items.find(i => i.id === itemId);

      if (item === undefined) return;

      const orderedQty   = parseFloat(item.quantity) || 1;
      const cancelledQty = parseFloat(data.cancelled_quantity) || 0;
      const effectiveQty = Math.max(0, orderedQty - cancelledQty);
      item.quantity = effectiveQty;
      item.cancelled_quantity = data.cancelled_quantity;
      item.restocked_quantity = data.restocked_quantity;
      item.cancelled_reason = data.cancelled_reason;
    },
    websocketDeleteItem(data) {
      const itemId = Number(data.id);
      for (let i = this.orders.length - 1; i >= 0; i--) {
        const order = this.orders[i];
        const idx = order.items.findIndex(it => it.id === itemId);
        if (idx === -1) continue;
        order.items.splice(idx, 1);
        if (order.items.length === 0) {
          this.orders.splice(i, 1);
        }
        break;
      }
    },
    websocketUpdateItem(data) {
      // event 'order.item.updated' มี 2 payload:
      // - {id, state} จาก KDS เปลี่ยนสถานะรายตัว → อัปเดตเฉพาะ state
      // - item เต็มตัว (มี quantity/options) จาก POS แก้ item → replace item รายตัวบนการ์ด
      //   (สลับได้เฉพาะ variant ใต้ parent เดียวกัน หมวด/สถานีไม่เปลี่ยน จึงไม่มีเคสเพิ่ม/ย้ายการ์ด)
      if (data.quantity !== undefined) {
        this.websocketReplaceItem(data);
        return;
      }
      const itemId = Number(data.id);
      for (let i = this.orders.length - 1; i >= 0; i--) {
        const order = this.orders[i];
        const item = order.items.find(i => i.id === itemId);
        if (item === undefined) continue;
        item.state = data.state;
        break;
      }
    },
    // หลังบ้านสั่งล้างของค้างทั้งสาขา — event ไม่ได้ส่ง id มาให้ (รอบเดียวกระทบได้หลักพัน item)
    // จอที่เปิดอยู่จึงต้องดึงรายการใหม่ทั้งก้อน แทนการไล่ลบทีละใบ
    websocketBulkCompleted() {
      this.fetchOrders();
    },
    websocketReplaceItem(data) {
      const itemId = Number(data.id);
      for (const order of this.orders) {
        const idx = order.items.findIndex(i => i.id === itemId);
        if (idx === -1) continue;

        const item = normalizeItem(data);
        // ธง "เพิ่งถูกแก้จาก POS" → การ์ดไฮไลต์+ป้าย แล้วเคลียร์ตัวเองหลัง 10 วิ
        // เก็บเป็น timestamp กันเคสแก้ซ้ำติด ๆ กัน: timer เก่าจะไม่ลบธงของรอบใหม่
        const stamp = Date.now();
        item.updated_flash = stamp;
        order.items.splice(idx, 1, item);

        playNotifySound(NOTIFY_SOUNDS.requested);
        setTimeout(() => {
          const current = order.items.find(i => i.id === itemId);
          if (current && current.updated_flash === stamp) current.updated_flash = 0;
        }, 10000);
        return;
      }
      // ไม่เจอ = ออเดอร์ไม่อยู่บนจอสถานีนี้ (คนละหมวด) → ไม่ต้องทำอะไร
    }
  },
  mounted() {
    const { branchId } = useKDSStore();
    const { isConnected, ensureConnected } = useKDSSocket({
      branchId,
      onOrderCreated: this.websocketAddOrder,
      onItemCancelled: this.websocketCancelItem,
      onItemDeleted: this.websocketDeleteItem,
      onItemUpdated: this.websocketUpdateItem,
      onItemsBulkCompleted: this.websocketBulkCompleted,
      onReconnect: this.onSocketReconnect
    });
    this.socketEnsureConnected = ensureConnected;

    // ref → data: ป้าย Live/Offline ต้องอ่านค่าจริง ไม่ใช่ตัว ref (ที่ truthy ตลอด)
    // socket ล่ม/มือถือหลับ → เดินด้วย polling ไปก่อน แล้วหยุดเมื่อกลับมาต่อได้
    this.stopWatchSocket = watch(isConnected, (isUp) => {
      this.socketConnected = isUp;
      if (isUp) this.stopPolling();
      else if (document.visibilityState === 'visible') this.startPolling();
    }, { immediate: true });

    this.fetchOrders();

    // resync ตอนกลับมาเห็นจอ (ปลดล็อกมือถือ/สลับกลับมาที่แท็บ) + ตอนเน็ตกลับมา
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.resyncIfVisible);
    window.addEventListener('online', this.resyncIfVisible);

    this.removeFsListener = onFullscreenChange(() => {
      this.isFullscreen = isFullscreen();
    });
  },
  beforeUnmount() {
    this.stopPolling();
    if (this.stopWatchSocket) this.stopWatchSocket();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.resyncIfVisible);
    window.removeEventListener('online', this.resyncIfVisible);
    if (this.removeFsListener) this.removeFsListener();
  }
}
</script>

